import {register} from '@shopify/theme-sections';
import Flickity from 'flickity';
import 'flickity-imagesloaded';
import 'flickity-as-nav-for';
import Player from '@vimeo/player';
import Dialog from 'a11y-dialog-component';

export default register('product-blocks', {
  onLoad() {
    this.initReviewStars();
    this.initProductInclusions();
    this.initProductFeatures();
    this.initProductTimeline();
    this.initRecipeRecommendations();
    this.initTrustIndicators();
    this.initTestimonials();
    this.initLogos();
    this.initVideos();
    this.initTabLinks();
  },

  initReviewStars() {
    const reviewStars = this.container.querySelectorAll('.okeReviews-ratingWrapper');
    reviewStars.forEach(stars => {
      stars.addEventListener('click', () => {
        const reviewTabButton = document.querySelector('#product-reviews-tab');
        if (!reviewTabButton) return;

        reviewTabButton.click();

        window.scrollTo({
          top: reviewTabButton.offsetTop - 100,
          left: 0,
          behavior: 'smooth',
        });
      });
    });
  },

  initProductInclusions() {
    const slider = this.container.querySelector('.product-inclusions__slider');
    if (!slider) {
      return;
    }

    new Flickity(slider, {
      prevNextButtons: false,
      pageDots: true,
      groupCells: true,
      cellAlign: 'left',
      contain: true,
      watchCSS: true,
    });

    this.productInclusionsModal = new Dialog('.modal--product-inclusions', {
      openingSelector: '.modal-product-inclusions-open',
      closingSelector: '.modal-product-inclusions-close',
      backdropSelector: '.overlay--product-inclusions',
      onOpen: function() {
        document.body.classList.add('modal--product-inclusions-open');
      },
      onClose: function() {
        document.body.classList.remove('modal--product-inclusions-open');
      },
    });

    const items = this.container.querySelectorAll('.product-inclusions__item');
    items.forEach(item => item.addEventListener('click', this.openProductInclusionsModal.bind(this)));
  },

  openProductInclusionsModal(event) {
    if (window.innerWidth >= window.theme.breakpoints.large) {
      return;
    }

    const item = event.currentTarget;
    const content = item.querySelector('.product-inclusions__content');
    if (!content) {
      return;
    }
    const modalContent = this.productInclusionsModal.dialog.querySelector('.modal__content');
    if (!modalContent) {
      return;
    }
    modalContent.innerHTML = content.innerHTML;
    this.productInclusionsModal.open();
  },

  initProductFeatures() {
    const slider = this.container.querySelector('.product-features__slider');
    const thumbnailsSlider = this.container.querySelector('.product-features__thumbnails-slider');
    if (!slider || !thumbnailsSlider) {
      return;
    }

    new Flickity(slider, {
      prevNextButtons: true,
      pageDots: false,
      cellAlign: 'left',
      arrowShape:
        'M75.8703 1.12651C74.364 -0.375504 71.9218 -0.375504 70.4154 1.12651L24.1297 47.2804C22.6234 48.7824 22.6234 51.2176 24.1297 52.7196L70.4154 98.8735C71.9218 100.376 74.364 100.376 75.8703 98.8735C77.3766 97.3715 77.3766 94.9362 75.8703 93.4342L32.312 50L75.8703 6.56579C77.3766 5.06378 77.3766 2.62853 75.8703 1.12651Z',
    });

    new Flickity(thumbnailsSlider, {
      asNavFor: slider,
      prevNextButtons: false,
      pageDots: false,
      cellAlign: 'center',
    });
  },

  initProductTimeline() {
    const slider = this.container.querySelector('.product-timeline__slider');
    if (!slider) {
      return;
    }

    new Flickity(slider, {
      prevNextButtons: false,
      pageDots: true,
      groupCells: true,
      cellAlign: 'left',
      contain: true,
      cellSelector: '.product-timeline__item',
    });
  },

  initRecipeRecommendations() {
    const slider = this.container.querySelector('.product-recipe-recommendations__slider');
    if (!slider) {
      return;
    }

    new Flickity(slider, {
      prevNextButtons: false,
      pageDots: true,
      groupCells: true,
      cellAlign: 'left',
      contain: true,
      watchCSS: true,
    });
  },

  initTrustIndicators() {
    const sliders = this.container.querySelectorAll('.trust-indicators__slider');
    sliders.forEach(slider => {
      new Flickity(slider, {
        prevNextButtons: false,
        pageDots: false,
        cellAlign: 'left',
        watchCSS: true,
      });
    });
  },

  initTestimonials() {
    const sliders = this.container.querySelectorAll('.testimonials__slider');
    sliders.forEach(slider => {
      new Flickity(slider, {
        prevNextButtons: false,
        pageDots: true,
        cellAlign: 'left',
        watchCSS: true,
      });
    });
  },

  initLogos() {
    const sliders = this.container.querySelectorAll('.logos__slider');
    sliders.forEach(slider => {
      new Flickity(slider, {
        prevNextButtons: false,
        pageDots: false,
        groupCells: true,
        cellAlign: 'left',
        contain: true,
        watchCSS: true,
      });
    });
  },

  initVideos() {
    const playButtons = this.container.querySelectorAll('.product-block__play-button');
    playButtons.forEach(playButton => playButton.addEventListener('click', this.playPauseVideo.bind(this)));

    const videos = this.container.querySelectorAll('[id^="product-block__video--"]');
    videos.forEach(video => this.initVimeoVideos(video));
  },

  playPauseVideo(event) {
    const playButton = event.currentTarget;
    const video = this.container.querySelector(`#${playButton.dataset.videoId}`);
    if (!video) {
      return;
    }

    if (video.paused) {
      video.play();
      playButton.classList.add('is-playing');
    } else {
      video.pause();
      playButton.classList.remove('is-playing');
    }
  },

  initVimeoVideos(video) {
    const videoSettings = JSON.parse(video.dataset.videoSettings);
    if (videoSettings.videoType == 'vimeo') {
      new Player(video, {
        id: videoSettings.videoId,
        loop: true,
        autoplay: videoSettings.videoControls == true ? false : true,
        controls: videoSettings.videoControls == true ? true : false,
        muted: videoSettings.videoControls == true ? false : true,
        playsinline: true,
        title: false,
        background: videoSettings.videoControls == true ? false : true, // only works on paid plans
      });
    }
  },

  initTabLinks() {
    const linksToTabs = this.container.querySelectorAll('a[href^="#"]');
    linksToTabs.forEach(linkToTab => linkToTab.addEventListener('click', this.openTab.bind(this)));
  },

  openTab(event) {
    const id = event.currentTarget.getAttribute('href').replace('#', '');
    const tabLink = document.querySelector(`[aria-controls="${id}"]`);

    if (!tabLink) {
      return;
    }

    event.preventDefault();

    tabLink.click();
  },
});
